import React, {useCallback, useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {DataObjectId, Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {IconButton} from '@components/common/base/buttons'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {ProjectsFlatTreeDropdownContent} from '@components/common/projects-tree/projects-tree'
import {TaskItem} from '@components/my-tasks/task-item'
import {BooleanHookState, useBoolean} from '@containers/hooks'
import {Task} from '@extensions/task'
import {Props} from './task-menu-new'
import {TaskMenuNewDropdown} from './task-menu-new-dropdown'

export interface InnerProps {
  toggleComplete(): void
  toggleArchive(): void
  deleteTask(): void
  moveTask?: (column: Hub[]) => void
  selectMultipleTasksToggleOn(): void
  canChangeBoardSettings?: boolean
  hub: Hub
  onToggle?: (value: boolean) => void
}

const TwIconButton = tw(IconButton)<{
  $isTaskColored?: boolean
  $isCompleted?: boolean
  $isOpen?: boolean
  $withBorder?: boolean
}>`
  relative
  
  ${p => {
    if (p.$withBorder) {
      return '!w-12 !h-20 flex justify-center items-center border border-solid rounded-[7px]'
    }
    return ''
  }}
  ${p => {
    if (p.$isTaskColored) {
      return `!text-icon-primary ${
        p.$isCompleted
          ? 'opacity-[.38] hover:opacity-[.64] active:opacity-[.64]'
          : 'opacity-[.64] hover:opacity-100 active:opacity-100'
      }`
    }

    return `hover:!text-action-hover active:!text-action-pressed ${
      p.$isCompleted ? '!text-placeholder-new' : ''
    }`
  }}
`

export const TaskMenuNewBase = (props: Props & InnerProps) => {
  const {task, moveTask, className, hub, onToggle, withBorder} = props
  const ref = useRef(null)
  const isTaskColored = Task.isTaskColored(hub)
  const moveOpen = useBoolean(false)

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const openMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsMenuOpen(true)
  }, [])
  const closeMenu = useCallback(() => setIsMenuOpen(false), [])

  useEffect(() => {
    onToggle?.(isMenuOpen)
  }, [isMenuOpen])

  return (
    <>
      <TwIconButton
        data-test="board-task-menu"
        ref={ref}
        icon={
          withBorder ? 'IconCustomDotsVerticalNoPadding' : 'IconDotsVertical'
        }
        color="secondary"
        size="small"
        wrapperSize="big"
        className={className}
        $isTaskColored={isTaskColored}
        $isCompleted={props.completed}
        $isOpen={isMenuOpen}
        $withBorder={withBorder}
        forcedState={isMenuOpen ? 'active' : undefined}
        onClick={openMenu}
      />

      {isMenuOpen && (
        <TaskMenuNewDropdown
          {...props}
          reference={ref}
          closeMenu={closeMenu}
          moveOpen={moveOpen}
        />
      )}

      <MovementDropdown
        reference={ref}
        task={task}
        moveTask={moveTask}
        moveOpen={moveOpen}
      />
    </>
  )
}

interface MovementDropdownProps {
  reference: React.RefObject<Element> | Element | null
  task: TaskItem
  moveTask?: (column: Hub[]) => void
  moveOpen: BooleanHookState
  callback?: (location: DataObjectId) => void
}

const MovementDropdown = (props: MovementDropdownProps) => {
  const {reference, task, moveTask, moveOpen, callback} = props
  if (!moveOpen.value) {
    return null
  }

  const chooseLocation = (locationId: DataObjectId) => {
    if (callback) {
      callback(locationId)
    }

    if (moveTask) {
      const box = Sync.get(task)
      const hub = box.getHub(locationId)

      hub && moveTask([hub])
    }

    moveOpen.turnOff()
  }

  return (
    <Dropdown
      reference={reference}
      hideArrow
      content={
        <ProjectsFlatTreeDropdownContent
          value={task.location}
          onChange={chooseLocation}
        />
      }
      visible={moveOpen.value}
      onVisibleChange={moveOpen.turnOff}
    />
  )
}
